import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ForgeCard,
  ForgeExpansionPanel,
  ForgeToolbar,
  ForgeIcon,
  ForgeBadge,
  ForgeButton,
  ForgeTooltip
} from '@tylertech/forge-react';
import { Context } from '../../../store/Store.js';
import ToolbarTitle from '../../reusable/Toolbars/ToolbarHelperComponents/ToolbarTitle.js';
import './ExpansionPanel.scss';
import { CLEAR } from '../../../static/keyWords.js';
import { handleKeyDown } from '../reusableFunctions.js';

const ExpansionPanel = ({
  title,
  fromBudgetAccessForUsers = false,
  children,
  width,
  maxWidth,
  titleClasses,
  open,
  withCard = true,
  chipCount,
  clearButton,
  iconClasses,
  role,
  ariaLabel,
  handleClearButton = () => { },
  contentClasses,
  forgeCardBodyContainerClass,
  anchorId = '',
  onClick = () => { }
}) => {
  const [isExpanded, setIsExpanded] = useState(open);
  const [state] = useContext(Context);

  useEffect(() => {
    if (state.keepBudgetAccessForUsersActive && fromBudgetAccessForUsers) {
      setIsExpanded(true);
    }
  }, [state.keepBudgetAccessForUsersActive]);

  return (
    <>
      {withCard ? (
        <ForgeCard
          no-padding
          className="forge-card-extended"
          style={{ width: width, maxWidth: maxWidth }}
        >
          <ForgeExpansionPanel
            tabIndex="0"
            role={role}
            aria-label={ariaLabel}
            open={isExpanded}
            on-forge-expansion-panel-toggle={(event) => {
              setIsExpanded(event.detail);
              onClick(event.detail);
            }}
            onkeydown={(e) => handleKeyDown(e, () => setIsExpanded(!isExpanded))}
          >
            <ForgeToolbar slot="header" no-divider={isExpanded ? false : true}>
              <ToolbarTitle
                title={title}
                titleClass={titleClasses}
              />
              {(typeof chipCount === 'number' && chipCount >= 0) && (
                <ForgeBadge className="marginLeft" theme="info">{chipCount}</ForgeBadge>
              )}
              <div id={anchorId} slot="end" className="defaultIconColor">

                {isExpanded ? (
                  <ForgeIcon external externalType="all" name="chevron_up" />
                ) : (
                  <ForgeIcon external externalType="all" name="chevron_down" />
                )}
              </div>
              <ForgeTooltip anchor={anchorId} placement="bottom">
                {isExpanded ? 'Close' : 'Open'}
              </ForgeTooltip>
            </ForgeToolbar>
            <div className={`forge-card-body-container ${forgeCardBodyContainerClass} ${contentClasses}`}>{children}</div>
          </ForgeExpansionPanel>
        </ForgeCard>
      ) : (
        <ForgeExpansionPanel
          tabIndex="0"
          role={role}
          aria-label={ariaLabel}
          open={isExpanded}
          on-forge-expansion-panel-toggle={(event) =>
            setIsExpanded(event.detail)
          }
          onkeydown={(e) => handleKeyDown(e, () => setIsExpanded(!isExpanded))}
        >
          <div slot="header" style={{ display: 'flex', justifyContent: 'space-between', width: width }}>
            <div className={titleClasses}>
              {title}
              {chipCount > 0 && (
                <ForgeBadge theme="info">{chipCount}</ForgeBadge>
              )}
            </div>
            <div id={anchorId} className={iconClasses}>
              {clearButton && (
                <ForgeButton
                  variant="primary"
                  onClick={handleClearButton}
                  onkeydown={(e) => handleKeyDown(e, handleClearButton) }
                >
                  {CLEAR}
                </ForgeButton>
              )}
              {isExpanded ? (
                <ForgeIcon className="defaultIconColor" external externalType="all" name="chevron_up" />
              ) : (
                <ForgeIcon className="defaultIconColor" external externalType="all" name="chevron_down" />
              )}
            </div>
            <ForgeTooltip anchor={anchorId} placement="bottom">
              {isExpanded ? 'Close' : 'Open'}
            </ForgeTooltip>
          </div>
          <div className={`forge-card-body-container ${forgeCardBodyContainerClass}`}>{children}</div>
        </ForgeExpansionPanel>
      )}
    </>
  );
};

ExpansionPanel.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  fromBudgetAccessForUsers: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  titleClasses: PropTypes.string,
  open: PropTypes.bool,
  withCard: PropTypes.bool,
  chipCount: PropTypes.number,
  clearButton: PropTypes.bool,
  iconClasses: PropTypes.string,
  handleClearButton: PropTypes.func,
  role: PropTypes.string,
  ariaLabel: PropTypes.string,
  contentClasses: PropTypes.string,
  forgeCardBodyContainerClass: PropTypes.string,
  anchorId: PropTypes.string,
  onClick: PropTypes.func,
};

export default ExpansionPanel;
